import React, { useEffect, useState } from "react";
import { Edit, Trash2, MessageSquare, Plus, Save, SaveAll } from "lucide-react";
import Loader from "@/components/Loader";
import useToastStore from "@/components/Common/Toast/toastStore";
import { noteService } from "@/services/notes";
import DialogBox from "@/components/Common/DialogBox";
const Notes = () => {
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [newNoteCategory, setNewNoteCategory] = useState({
    apptkr_title: "",
    bullhorn_title: "",
  });
  const { showToast } = useToastStore;
  // State to track additional form fields
  const [additionalFields, setAdditionalFields] = useState([]);

  // State to track if fields should be shown
  const [showAddNote, setShowAddNote] = useState(false);
  const [showDeleteNote, setShowDeleteNote] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editNote, setEditNote] = useState(null);

  const fetchNotes = async () => {
    try {
      setIsLoading(true);

      const response = await noteService.fetchNotes();
      console.log("API Response:", response); // Log the API response

      // Check if response exists and has the expected structure
      if (!response) {
        console.error("Invalid response: No data received");
        showToast({
          message: "Failed to load notes: No data received",
          type: "error",
        });
        setNotes([]);
        setFilteredNotes([]);
        return;
      }

      // Handle different response formats
      let dataToProcess = [];

      if (Array.isArray(response)) {
        dataToProcess = response;
      } else if (response.data && Array.isArray(response.data)) {
        dataToProcess = response.data;
      } else if (response.categories && Array.isArray(response.categories)) {
        dataToProcess = response.categories;
      } else {
        console.error("Unexpected response format:", response);
        showToast({
          message: "Failed to load notes: Unexpected data format",
          type: "error",
        });
        setNotes([]);
        setFilteredNotes([]);
        return;
      }

      // Map the API response to match our component's expected format
      const formattedNotes = dataToProcess.map((note) => ({
        id: note.id,
        title: note.title || "",
        content: note.content || "",
        apptkr_title: note.apptkr_title || note.category || "",
        bullhorn_title: note.bullhorn_title || note.bullhorn_mapping || "",
        created_at: note.created_at || new Date().toISOString(),
        updated_at: note.updated_at || "",
        created_by: note.created_by || { name: "" },
      }));

      console.log("Formatted Notes:", formattedNotes); // Log the formatted notes

      setNotes(formattedNotes);
      setFilteredNotes(formattedNotes);

      // Debug state after setting
      setTimeout(() => {
        console.log("Notes state after update:", notes);
        console.log("Notes length:", notes.length);
      }, 100);
    } catch (error) {
      console.error("Error fetching notes:", error);
      showToast({
        message: `Failed to load notes: ${error.message || "Unknown error"}`,
        type: "error",
      });
      setNotes([]);
      setFilteredNotes([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, []);

  // Reload notes after saving or updating
  const reloadNotes = () => {
    fetchNotes();
  };

  // Handle Add Note button click
  const handleAddNote = () => {
    if (!showAddNote) {
      // First click - show the fields
      setShowAddNote(true);
      setNewNoteCategory({ apptkr_title: "", bullhorn_title: "" });
    } else {
      // Subsequent clicks - add more fields
      handleAddFields();
    }
    setIsSaving(false);
  };

  const handleEditNote = (note) => {
    setEditNote({ ...note });
    setShowAddNote(true);
    setNewNoteCategory({
      apptkr_title: note.apptkr_title,
      bullhorn_title: note.bullhorn_title,
    });
  };

  const handleUpdateNote = async () => {
    if (!editNote?.id) {
      showToast({
        message: "Please select a note to update first",
        type: "error",
      });
      return;
    }

    try {
      setIsSaving(true);

      // Call API to update the note
      await noteService.updateNote(editNote.id, newNoteCategory);

      // Update local state
      const updatedNotes = notes.map((note) =>
        note.id === editNote.id
          ? {
              ...note,
              apptkr_title: newNoteCategory.apptkr_title,
              bullhorn_title: newNoteCategory.bullhorn_title,
              updated_at: new Date().toISOString(),
            }
          : note,
      );

      setNotes(updatedNotes);
      setFilteredNotes(updatedNotes);

      // Reset states
      setEditNote(null);
      setNewNoteCategory({
        apptkr_title: "",
        bullhorn_title: "",
      });
      setShowAddNote(false);

      showToast({
        message: "Note updated successfully!",
        type: "success",
      });

      // Reload notes after successful update
      reloadNotes();
    } catch (error) {
      console.error(`Failed to update note: ${error.message}`);
      showToast({
        message: `Failed to update note: ${error.message}`,
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const showDeleteNoteDialog = (noteId) => {
    setNoteToDelete(noteId);
    setShowDeleteNote(true);
  };
  const handleDeleteNote = async () => {
    try {
      setIsDeleting(true);
      // Call API to delete the note
      await noteService.deleteNote(noteToDelete);

      // Update local state after successful deletion
      const updatedNotes = notes.filter((note) => note.id !== noteToDelete);
      setNotes(updatedNotes);
      setFilteredNotes(updatedNotes);
      setNoteToDelete(null);

      showToast({
        message: "Note deleted successfully!",
        type: "success",
      });

      // Reload notes after successful deletion
      reloadNotes();
    } catch (error) {
      showToast({
        message: `Failed to delete note: ${error.message}`,
        type: "error",
      });
    } finally {
      setShowDeleteNote(false);
      setIsDeleting(false);
    }
  };

  // Handle save button click to call API
  const handleSaveNotes = async () => {
    try {
      setIsSaving(true);

      // // Collect payload data from main fields and additional fields
      // const payload = [];

      // // Add main fields to payload if they have values
      // if (newNoteCategory.apptkr_title && newNoteCategory.bullhorn_title) {
      //   payload.push({
      //     apptkr_title: newNoteCategory.apptkr_title,
      //     bullhorn_title: newNoteCategory.bullhorn_title
      //   });
      // }

      // Send the payload to the API
      await noteService.createNotes({ categories: [newNoteCategory] });

      // Mark save as completed to hide the icon
      setIsSaving(false);
      setShowAddNote(false);

      showToast({
        message: "Notes Category saved successfully!",
        type: "success",
        autoClose: 3000,
        closeOnClick: true,
      });

      // Reload notes after successful save
      reloadNotes();
    } catch (error) {
      showToast({
        message: "Failed to save notes category: " + error.message,
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-background">
        <Loader bgColor="transparent" loadingText="Loading notes..." />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <div className="flex-1 w-[90%] pt-12 px-8 mx-auto flex flex-col gap-8">
        {/* Header Section */}
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-foreground text-2xl font-semibold font-['Inter'] leading-8">
                Notes Categories
              </h1>
              <p className="text-muted-foreground text-base font-normal font-['Inter'] leading-6">
                Manage note categories, configure mappings, and track
                recruitment communication.
              </p>
            </div>
            <button
              onClick={handleAddNote}
              className="bg-primary-dark h-[36px] text-white px-4 p rounded-lg shadow-sm font-semibold transition-all hover:bg-primary-dark"
              id="add-note-btn"
            >
              Add Note
            </button>
          </div>
        </div>

        {!notes || notes.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <h3 className="text-lg font-medium text-muted-foreground">
              No notes found. Create a new note to get started.
            </h3>
          </div>
        ) : (
          <div className="overflow-x-auto bg-card rounded-lg shadow">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted border-b border-border">
                <tr className="text-left">
                  <th className="px-6 py-5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Bullhorn Mapping
                  </th>
                  <th className="px-6 py-5 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {notes.map((note) => (
                  <tr
                    className="hover:bg-muted transition-colors duration-200"
                    key={note.id}
                  >
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-foreground">
                      {note.apptkr_title}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-muted-foreground">
                      {note.bullhorn_title}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end items-center space-x-2">
                        <button
                          onClick={() => handleEditNote(note)}
                          className="text-muted-foreground hover:text-muted-foreground transition-colors"
                          title="Edit Note"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => showDeleteNoteDialog(note.id)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                          title="Delete Note"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DialogBox
          isOpen={showAddNote}
          onClose={() => {
            setShowAddNote(false);
            setNewNoteCategory({ apptkr_title: "", bullhorn_title: "" });
            setEditNote(null);
          }}
          title={editNote ? "Edit Note" : "Add Note"}
          className="w-[40%] max-md:w-[60%]"
          showIcon={false}
          actionButton={
            <button
              onClick={editNote ? handleUpdateNote : handleSaveNotes}
              className={`bg-primary-dark text-white px-5 py-2 rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-2 ${isSaving ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={isSaving}
            >
              {editNote ? "Update Note" : "Save Note Category"}
            </button>
          }
        >
          {/* Note Form Section - only shown after Add Note is clicked */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Category
              </label>
              <input
                type="text"
                placeholder="Enter category"
                className="border border-input bg-background text-foreground placeholder:text-muted-foreground rounded-lg p-2 w-full outline-none focus:ring-1 focus:ring-ring"
                autoComplete="off"
                value={newNoteCategory.apptkr_title}
                onChange={(e) =>
                  setNewNoteCategory({
                    ...newNoteCategory,
                    apptkr_title: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                BullHorn Mapping
              </label>
              <input
                type="text"
                placeholder="Enter BullHorn mapping"
                className="border border-input bg-background text-foreground placeholder:text-muted-foreground rounded-lg p-2 w-full outline-none focus:ring-1 focus:ring-ring"
                autoComplete="off"
                value={newNoteCategory.bullhorn_title}
                onChange={(e) =>
                  setNewNoteCategory({
                    ...newNoteCategory,
                    bullhorn_title: e.target.value,
                  })
                }
              />
            </div>
          </div>
        </DialogBox>

        {/* Delete Confirmation Modal */}
        <DialogBox
          isOpen={showDeleteNote}
          onClose={() => {
            setShowDeleteNote(false);
            setNoteToDelete(null);
          }}
          title="Delete Note"
          className="w-[40%] max-md:w-[60%]"
          actionButton={
            <button
              onClick={handleDeleteNote}
              className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto"
              disabled={isDeleting}
            >
              Delete
            </button>
          }
          children={
            <p className=" text-base text-muted-foreground">
              Are you sure you want to delete this note? This action cannot be
              undone.
            </p>
          }
        />
      </div>
    </div>
  );
};

export default Notes;
